import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Title,
  Text,
  Grid,
  Card,
  Metric,
  BarList,
  LineChart,
  BarChart,
  AreaChart,
  TextInput,
  Badge,
} from '@tremor/react';
import { Search } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { CohortMatrix, CohortBucket } from '@/components/CohortMatrix';
import { getAtividade, extractErrorMessage } from '@/lib/api';

// ── Tipos (payload de GET /atividade?days=N) ────────────────────────────────

interface TendenciaPonto {
  dia: string;
  dau: number;
  wau: number;
  mau: number;
  stickiness: number | null;
}

interface Tendencia {
  serie: TendenciaPonto[];
  avg_dau: number;
  avg_mau: number;
  stickiness: number | null;
}

interface DecompPonto {
  dia: string;
  novo: number;
  recorrente: number;
  ressuscitado: number;
}

interface Decomposicao {
  serie: DecompPonto[];
  dormentes: number;
  janela_dias: number;
}

interface Degrau {
  key: string;
  label: string;
  n: number;
}

interface PowerBucket {
  faixa: string;
  usuarios: number;
}

interface EmRiscoRow {
  user_id: number;
  email: string | null;
  org_id: number | null;
  org_name: string | null;
  creditos_org: number;
  dias_ativos: number;
  ultimo_evento: string;
  ultimo_evento_em: string;
  dias_sumido: number;
}

interface NorthStarSemana {
  semana: string;
  usuarios: number;
  corrente: boolean;
}

interface AtividadeData {
  disponivel: boolean;
  erro?: string;
  days: number;
  hoje: string;
  tendencia: Tendencia;
  decomposicao: Decomposicao;
  piramide: { total: number; degraus: Degrau[] };
  power_curve: PowerBucket[];
  cohorts: CohortBucket[];
  em_risco: EmRiscoRow[];
  north_star: { semana_corrente: number; serie: NorthStarSemana[] };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const PERIODOS = [7, 14, 30, 90];

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtDia(iso: string): string {
  // '2026-07-05' → '05/07'
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

export function AtividadePage() {
  const [days, setDays] = useState(30);
  const [showWau, setShowWau] = useState(true);
  const [showMau, setShowMau] = useState(true);
  const [busca, setBusca] = useState('');

  const { data, isLoading, error } = useQuery<AtividadeData>({
    queryKey: ['atividade', days],
    queryFn: async () => (await getAtividade(days)).data,
    refetchInterval: 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar atividade')
    : data && !data.disponivel
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const t = data?.tendencia;
  const dec = data?.decomposicao;
  const ns = data?.north_star;

  const tendenciaChart = useMemo(
    () =>
      (t?.serie ?? []).map((p) => ({
        dia: fmtDia(p.dia),
        DAU: p.dau,
        ...(showWau ? { WAU: p.wau } : {}),
        ...(showMau ? { MAU: p.mau } : {}),
      })),
    [t, showWau, showMau],
  );

  const stickinessChart = useMemo(
    () =>
      (t?.serie ?? []).map((p) => ({
        dia: fmtDia(p.dia),
        'DAU/MAU': p.stickiness !== null ? +(p.stickiness * 100).toFixed(1) : 0,
      })),
    [t],
  );

  const decompChart = useMemo(
    () =>
      (dec?.serie ?? []).map((p) => ({
        dia: fmtDia(p.dia),
        Novo: p.novo,
        Recorrente: p.recorrente,
        Ressuscitado: p.ressuscitado,
      })),
    [dec],
  );

  const nsChart = useMemo(
    () =>
      (ns?.serie ?? []).map((p) => ({
        semana: fmtDia(p.semana),
        'Usuários com valor': p.usuarios,
      })),
    [ns],
  );

  const piramideItems = (data?.piramide.degraus ?? []).map((d) => ({
    name: d.label,
    value: d.n,
  }));

  const powerChart = (data?.power_curve ?? []).map((b) => ({
    faixa: `${b.faixa} dias`,
    Usuários: b.usuarios,
  }));

  const riscoRows = useMemo(() => {
    const rows = data?.em_risco ?? [];
    const q = busca.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.email ?? '').toLowerCase().includes(q) ||
        (r.org_name ?? '').toLowerCase().includes(q) ||
        r.ultimo_evento.toLowerCase().includes(q),
    );
  }, [data, busca]);

  const chartCategories = [
    'DAU',
    ...(showWau ? ['WAU'] : []),
    ...(showMau ? ['MAU'] : []),
  ];
  const chartColors: ('blue' | 'violet' | 'slate')[] = [
    'blue',
    ...(showWau ? (['violet'] as const) : []),
    ...(showMau ? (['slate'] as const) : []),
  ];

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <Title>Atividade</Title>
          <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDays(p)}
                className={`rounded px-2.5 py-1 text-xs transition ${
                  days === p
                    ? 'bg-blue-600/30 text-blue-200'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
        <Text>
          Comportamento de usuários (ativos ou não). Ativo no dia = ≥1 evento
          logado no dia. Contas internas/teste excluídas de tudo.
        </Text>
      </header>

      {/* North-star + Stickiness */}
      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card className="border border-blue-700/40 bg-blue-900/10">
          <Text className="text-blue-200">
            North-star — usuários que geraram VALOR nesta semana
          </Text>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <>
              <Metric className="text-blue-300">
                {fmtInt(ns?.semana_corrente)}
              </Metric>
              <Text className="text-xs text-slate-400">
                baixaram ou editaram um cálculo na semana corrente
              </Text>
              {nsChart.length > 0 && (
                <AreaChart
                  className="mt-2 h-24"
                  data={nsChart}
                  index="semana"
                  categories={['Usuários com valor']}
                  colors={['blue']}
                  showLegend={false}
                  showYAxis={false}
                  valueFormatter={fmtInt}
                />
              )}
            </>
          )}
        </Card>

        <Card>
          <Text>Stickiness (DAU/MAU médio do período)</Text>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <>
              <Metric>{fmtPct(t?.stickiness)}</Metric>
              <Text className="text-xs text-slate-400">
                DAU médio {t?.avg_dau ?? '—'} · MAU médio {t?.avg_mau ?? '—'}
              </Text>
              {stickinessChart.length > 0 && (
                <AreaChart
                  className="mt-2 h-24"
                  data={stickinessChart}
                  index="dia"
                  categories={['DAU/MAU']}
                  colors={['emerald']}
                  showLegend={false}
                  showYAxis={false}
                  valueFormatter={(v) => `${v}%`}
                />
              )}
            </>
          )}
        </Card>
      </Grid>

      {/* Tendência de ativos */}
      <Card>
        <div className="flex items-center justify-between">
          <Title>Tendência de ativos</Title>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowWau((v) => !v)}
              className={`rounded px-2 py-0.5 text-xs ${
                showWau
                  ? 'bg-violet-600/30 text-violet-200'
                  : 'bg-slate-800 text-slate-500'
              }`}
            >
              WAU 7d
            </button>
            <button
              type="button"
              onClick={() => setShowMau((v) => !v)}
              className={`rounded px-2 py-0.5 text-xs ${
                showMau
                  ? 'bg-slate-600/40 text-slate-200'
                  : 'bg-slate-800 text-slate-500'
              }`}
            >
              MAU 28d
            </button>
          </div>
        </div>
        <Text className="text-xs text-slate-400">
          DAU por dia; WAU/MAU = janelas móveis de 7/28 dias.
        </Text>
        {isLoading && <Text>carregando…</Text>}
        {!isLoading && errMsg && (
          <div className="text-sm text-red-400" role="alert">
            {errMsg}
          </div>
        )}
        {!isLoading && !errMsg && (
          <LineChart
            className="mt-3 h-64"
            data={tendenciaChart}
            index="dia"
            categories={chartCategories}
            colors={chartColors}
            valueFormatter={fmtInt}
            showAnimation
          />
        )}
      </Card>

      {/* Decomposição do DAU + dormentes */}
      <Grid numItems={1} numItemsLg={3} className="gap-4">
        <Card className="lg:col-span-2">
          <Title>Decomposição do DAU</Title>
          <Text className="text-xs text-slate-400">
            Novo = 1º dia de vida · Recorrente = ativo tb nos 28d anteriores ·
            Ressuscitado = voltou após ≥28d sumido.
          </Text>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <BarChart
              className="mt-3 h-64"
              data={decompChart}
              index="dia"
              categories={['Novo', 'Recorrente', 'Ressuscitado']}
              colors={['emerald', 'blue', 'amber']}
              stack
              valueFormatter={fmtInt}
            />
          )}
        </Card>
        <KPICard
          label={`Dormentes (≥${dec?.janela_dias ?? 28}d sem evento)`}
          value={isLoading ? null : fmtInt(dec?.dormentes)}
          loading={isLoading}
          error={errMsg}
          hint="usuários com histórico de atividade que sumiram"
        />
      </Grid>

      {/* Pirâmide + Power curve */}
      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <Title>Pirâmide de engajamento</Title>
          <Text className="text-xs text-slate-400">
            Cada usuário ativo no período conta UMA vez no degrau mais alto:
            Pagante &gt; Editor &gt; Uploader &gt; Visualizador &gt; Visitante.
          </Text>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <>
              <div className="mb-1 mt-3 flex items-center justify-between">
                <Text className="text-xs uppercase tracking-wide text-slate-400">
                  degrau
                </Text>
                <Text className="text-xs uppercase tracking-wide text-slate-400">
                  {fmtInt(data?.piramide.total)} usuários ativos
                </Text>
              </div>
              <BarList data={piramideItems} valueFormatter={fmtInt} color="blue" />
            </>
          )}
        </Card>

        <Card>
          <Title>Power users (L28)</Title>
          <Text className="text-xs text-slate-400">
            Nº de usuários por faixa de dias-ativos nos últimos 28 dias.
          </Text>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <BarChart
              className="mt-3 h-56"
              data={powerChart}
              index="faixa"
              categories={['Usuários']}
              colors={['violet']}
              valueFormatter={fmtInt}
            />
          )}
        </Card>
      </Grid>

      {/* Cohorts de retenção semanal */}
      <CohortMatrix
        cohorts={data?.cohorts ?? []}
        loading={isLoading}
        error={errMsg}
        title="Cohorts de retenção semanal"
        description="Linha = semana de cadastro (últimas 12). Colunas = % da cohort ativa na semana W0 (cadastro) até W8."
        metric="% com ≥1 evento logado na semana"
      />

      {/* Em risco */}
      <Card>
        <div className="flex items-center justify-between gap-4">
          <div>
            <Title>Em risco</Title>
            <Text className="text-xs text-slate-400">
              ≥2 dias ativos históricos, sumidos há 7–60 dias · ordenado por
              dias ativos (máx 50).
            </Text>
          </div>
          <div className="w-64">
            <TextInput
              icon={Search}
              placeholder="buscar e-mail / org / evento"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
        </div>
        {isLoading && <Text>carregando…</Text>}
        {!isLoading && errMsg && (
          <div className="text-sm text-red-400" role="alert">
            {errMsg}
          </div>
        )}
        {!isLoading && !errMsg && (
          <div className="mt-3 overflow-x-auto">
            {riscoRows.length === 0 ? (
              <Text className="text-sm text-slate-400">
                ninguém em risco no recorte atual
              </Text>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="py-2 pr-4 font-medium">E-mail</th>
                    <th className="py-2 pr-4 font-medium">Org</th>
                    <th className="py-2 pr-4 font-medium">Dias ativos</th>
                    <th className="py-2 pr-4 font-medium">Último evento</th>
                    <th className="py-2 pr-4 font-medium">Sumido há</th>
                    <th className="py-2 pr-4 font-medium">Créditos org</th>
                  </tr>
                </thead>
                <tbody>
                  {riscoRows.map((r) => (
                    <tr
                      key={r.user_id}
                      className="border-b border-slate-800/50 text-slate-200"
                    >
                      <td className="py-2 pr-4">{r.email ?? '—'}</td>
                      <td className="py-2 pr-4">{r.org_name ?? '—'}</td>
                      <td className="py-2 pr-4 font-medium">{r.dias_ativos}</td>
                      <td className="py-2 pr-4">
                        <span className="font-mono text-xs">{r.ultimo_evento}</span>{' '}
                        <span className="text-xs text-slate-400">
                          {fmtDia(r.ultimo_evento_em)}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        <Badge
                          color={r.dias_sumido >= 28 ? 'red' : 'amber'}
                          size="xs"
                        >
                          {r.dias_sumido}d
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">{fmtInt(r.creditos_org)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
